import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL, buildKioskAuthHeaders } from './api';
import BefidDeviceCustomer from './BefidDeviceCustomer';
import NumericKeypad from './NumericKeypad';
import { clearCustomerSession, getCustomerSessionAccountId, saveCustomerSession } from './kioskCustomerSession';
import { isOrgasmeatExperience, ORGASMEAT_TOKENS, OrgasmeatBrand, useOrgasmeatSettings } from './OrgasmeatExperience';

function LoyaltyEntryScreen() {
  const orgasmeat = isOrgasmeatExperience(useOrgasmeatSettings());
  const navigate = useNavigate();
  const inactivityTimerRef = useRef(null);
  const activeRef = useRef(true);
  useEffect(() => { activeRef.current = true; return () => { activeRef.current = false; }; }, []);
  const [mode, setMode] = useState('');
  const [showLoyaltyOptions, setShowLoyaltyOptions] = useState(false);
  const [phone, setPhone] = useState('');
  const [deviceRequestPending, setDeviceRequestPending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const formattedPhone = useMemo(() => {
    return phone.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
  }, [phone]);

  useEffect(() => {
    if (deviceRequestPending) return undefined;
    const goBackToAttract = () => {
      clearCustomerSession();
      navigate('/veille');
    };

    const resetInactivityTimer = () => {
      if (inactivityTimerRef.current) {
        window.clearTimeout(inactivityTimerRef.current);
      }
      inactivityTimerRef.current = window.setTimeout(goBackToAttract, 20000);
    };

    const events = ['pointerdown', 'pointermove', 'keydown', 'touchstart', 'click', 'scroll'];
    events.forEach((eventName) => window.addEventListener(eventName, resetInactivityTimer, { passive: true }));
    resetInactivityTimer();

    return () => {
      events.forEach((eventName) => window.removeEventListener(eventName, resetInactivityTimer));
      if (inactivityTimerRef.current) {
        window.clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [navigate, deviceRequestPending]);

  const handleContinueAsGuest = () => {
    clearCustomerSession();
    navigate('/client');
  };

  const handleSubmit = async () => {
    if (phone.length < 10) {
      setError('Entre un numéro de téléphone valide.');
      return;
    }

    const accountId = getCustomerSessionAccountId();
    if (!accountId) { setError('Cette borne n’est pas prête. Demande de l’aide à l’équipe.'); return; }
    setLoading(true);
    setError('');

    try {
      const endpoint = mode === 'register' ? 'register' : 'login';
      const response = await fetch(`${API_URL}/api/customers/${endpoint}`, {
        method: 'POST',
        credentials: 'omit', cache: 'no-store',
        headers: buildKioskAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ phone }),
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Impossible de continuer.');
      }

      if (!activeRef.current || accountId !== getCustomerSessionAccountId()) return;
      if (!saveCustomerSession(payload, accountId)) throw new Error('Enregistrement du compte client impossible sur cet appareil.');
      navigate('/client');
    } catch (submitError) {
      if (activeRef.current && accountId === getCustomerSessionAccountId()) setError(submitError.message);
    } finally {
      setLoading(false);
    }
  };

  const loyaltyOptions = <>
    <BefidDeviceCustomer channel="borne" onPendingChange={setDeviceRequestPending} onCustomer={(customer) => {
      if (saveCustomerSession(customer, getCustomerSessionAccountId(), { ephemeral: true })) navigate('/client');
      else setError('La fiche client ne peut pas être utilisée. Réessayez.');
    }} />
    <button type="button" className="kiosk-cta" onClick={() => setMode('login')}>Mon compte</button>
    <button type="button" className="kiosk-cta secondary" onClick={() => setMode('register')}>Creer un compte</button>
  </>;

  return (
    <div className={`loyalty-entry-page${orgasmeat ? ' kiosk-orgasmeat orgasmeat-welcome' : ''}`} style={orgasmeat ? ORGASMEAT_TOKENS : undefined}>
      {orgasmeat && <OrgasmeatBrand />}
      <div className="loyalty-entry-card">
        {orgasmeat && <div className="orgasmeat-welcome-art" aria-hidden="true"><img src="/orgasmeat/burger-3.avif" alt="" /><span>HELLO<br />GOURMAND.</span></div>}
        <span className="kiosk-eyebrow">{orgasmeat ? 'Bienvenue chez ORGASMEAT' : 'Fidelisation'}</span>
        <h1>{orgasmeat ? 'Prêt pour le plaisir ?' : 'Mon compte'}</h1>
        <p>{orgasmeat ? 'Ta faim n’attend pas. Ton compte fidélité, c’est toi qui vois.' : 'Connecte-toi avec ton numero de telephone ou continue directement vers la borne.'}</p>

        {!mode ? (
          <div className="loyalty-entry-actions">
            {orgasmeat && <button type="button" className="orgasmeat-action" onClick={handleContinueAsGuest}>C’est parti, je commande <span aria-hidden="true">↗</span></button>}
            {orgasmeat ? <>
              <button type="button" className="kiosk-cta secondary" aria-expanded={showLoyaltyOptions} aria-controls="orgasmeat-loyalty-options" disabled={deviceRequestPending} onClick={() => setShowLoyaltyOptions(value => !value)}>J’ai un compte fidélité</button>
              {showLoyaltyOptions && <div id="orgasmeat-loyalty-options" className="orgasmeat-loyalty-options">{loyaltyOptions}</div>}
            </> : loyaltyOptions}
            {!orgasmeat && <button type="button" className="kiosk-cta secondary" onClick={handleContinueAsGuest}>
              Continuer sans se connecter
            </button>}
          </div>
        ) : (
          <>
            <div className="loyalty-entry-head">
              <div>
                <h2>{mode === 'register' ? 'Creer un compte' : 'Connexion'}</h2>
                <p>Utilise ton numéro de téléphone pour retrouver ton compte.</p>
              </div>
              <button type="button" className="kiosk-modal-close" onClick={() => { setMode(''); setPhone(''); setError(''); }}>
                ×
              </button>
            </div>

            <div className="loyalty-phone-display">
              <span>{formattedPhone}</span>
            </div>

            <NumericKeypad
              value={phone}
              maxLength={10}
              onChange={(nextPhone) => {
                setError('');
                setPhone(nextPhone);
              }}
            />

            {error && <div className="loyalty-entry-error">{error}</div>}

            <div className="loyalty-entry-actions loyalty-entry-actions-inline">
              <button type="button" className="kiosk-cta secondary" onClick={handleContinueAsGuest}>
                Continuer sans se connecter
              </button>
              <button type="button" className="kiosk-cta" onClick={handleSubmit} disabled={loading}>
                {loading ? 'Chargement...' : mode === 'register' ? 'Creer le compte' : 'Se connecter'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default LoyaltyEntryScreen;
